/**
 * Shared layout class fragments for marketing v2. Import these instead of
 * hard-coding px/py/max-w values in section components. Keeps public
 * routes aligned when rhythm changes.
 */

/** Horizontal gutters: px-6 lg:px-10 */
export const MARKETING_GUTTER_X = 'px-6 lg:px-10'

/** Default section vertical rhythm: py-12 md:py-16 */
export const MARKETING_SECTION_Y = 'py-12 md:py-16'

/** Footer vertical padding (sits below the final section band) */
export const MARKETING_FOOTER_Y = 'pt-16 md:pt-24 pb-10 md:pb-14'

/** Interior page hero top/bottom (PageHero) */
export const MARKETING_PAGE_HERO_Y = 'pt-32 pb-12 md:pt-40 md:pb-16'

/** Home landing hero vertical padding (below floating navbar) */
export const MARKETING_HOME_HERO_Y = 'pt-24 pb-12 md:pt-28 md:pb-16 lg:pt-32'

/** Floating navbar scroll threshold (px) */
export const MARKETING_NAV_SCROLL_THRESHOLD = 10

/** Header inset from top when floating (px) */
export const MARKETING_NAV_TOP_OFFSET = 8

/**
 * Animated max-width of centered header wrapper (px). Top state is sized so
 * the pill (wrapper minus the `md:px-8` gutter = 64px) spans exactly the
 * `max-w-6xl` (1152px) content column, keeping the floating nav within the
 * page's vertical guide lines.
 */
export const MARKETING_NAV_HEADER_MAX_TOP = 1216
export const MARKETING_NAV_HEADER_MAX_SCROLLED = 1000

/** Mobile pill horizontal margin (px) */
export const MARKETING_NAV_MOBILE_MARGIN_TOP = 8
export const MARKETING_NAV_MOBILE_MARGIN_SCROLLED = 16

export const MARKETING_NAV_TRANSPARENT_SHADOW =
  'var(--marketing-nav-transparent-shadow)'
export const MARKETING_NAV_SCROLLED_SHADOW =
  'var(--marketing-nav-scrolled-shadow)'
export const MARKETING_NAV_CANVAS_SHADOW = 'var(--marketing-nav-canvas-shadow)'

/**
 * Pill fill: theme-aware opaque surface. Light mode stays cream-tinted;
 * dark mode uses the near-black app background with a slightly stronger
 * scrolled fill so the pill reads as a surface without getting heavy.
 */
export const MARKETING_NAV_TOP_BG = 'var(--marketing-nav-top-bg)'
export const MARKETING_NAV_SCROLLED_BG = 'var(--marketing-nav-scrolled-bg)'
export const MARKETING_NAV_CANVAS_BG = 'var(--background)'

/**
 * Page / table / search fill that matches the unauthenticated cream canvas
 * (`--cream` in light mode, `--background` in dark). Use this instead of
 * `bg-background` (pure white) on public marketing surfaces so rows, sticky
 * chrome, and search pills don't punch white holes in the off-white page.
 */
export const MARKETING_CANVAS_BG = 'bg-cream dark:bg-background'

/** Article hero vertical padding */
export const MARKETING_ARTICLE_HERO_Y = 'pt-28 pb-10 md:pt-36 md:pb-14'

export const MARKETING_MAX_W_WIDE = 'max-w-6xl'
export const MARKETING_MAX_W_NARROW = 'max-w-5xl'
export const MARKETING_MAX_W_PROSE = 'max-w-3xl'
export const MARKETING_MAX_W_HOME_VISUAL = 'max-w-6xl'

/**
 * Editorial typography ladder. Every section component must consume one of
 * these (never hand-type `text-*` scales for headings) so heading rhythm
 * stays consistent across the site.
 *
 * The ladder mirrors cursor.com's section grammar:
 *   - HERO: top-of-page display headlines (HeroV2, ArticleHero, EditorialDivider)
 *   - SECTION: mid-page section headers (PageHero, MarketingSection, FaqSection, FinalCta)
 *   - BLOCK: alternating feature-row copy column (FeatureBlock)
 *   - TILE: card/tile titles inside grids (FeatureBento, FrontierCluster)
 */
export const MARKETING_HERO_HEADING =
  'marketing-display text-4xl md:text-6xl lg:text-7xl'
export const MARKETING_SECTION_HEADING =
  'marketing-display text-3xl md:text-5xl lg:text-6xl'
export const MARKETING_BLOCK_HEADING =
  'marketing-display text-2xl md:text-3xl lg:text-4xl'
export const MARKETING_TILE_HEADING =
  'text-xl md:text-2xl font-medium tracking-tight'

export type MarketingContainerSize =
  | 'wide'
  | 'default'
  | 'narrow'
  | 'prose'
  | 'visual'

const CONTAINER_MAX_W: Record<MarketingContainerSize, string> = {
  wide: MARKETING_MAX_W_WIDE,
  default: MARKETING_MAX_W_WIDE,
  narrow: MARKETING_MAX_W_NARROW,
  prose: MARKETING_MAX_W_PROSE,
  visual: MARKETING_MAX_W_HOME_VISUAL,
}

/** mx-auto w-full + max-width tier */
export function marketingContainerMaxWidth(
  size: MarketingContainerSize = 'default'
): string {
  return `mx-auto w-full ${CONTAINER_MAX_W[size]}`
}
